import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { providerReferralSchema, type ProviderReferralFormData } from 'app/utils/schemas'
import { usePhoneFormat } from 'app/utils/hooks/usePhoneFormat'
import { Label } from 'app/components/ui/label'
import { Input } from 'app/components/ui/input'
import { Textarea } from 'app/components/ui/textarea'
import { Button } from 'app/components/ui/button'
import { Dialog, DialogTrigger, DialogHeader, DialogTitle, DialogClose, DialogContent } from 'app/components/ui/dialog'
import { toast } from 'app/components/ui/use-toast'
import ToothSelector from './ToothSelected'
import { useReferralListSlice } from '../slice'

interface Props {
  onSave: () => void;
}

export const AddNewProviderReferral = ({ onSave }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const { useAddReferralMutation } = useReferralListSlice();
  const [addReferral, { isLoading, isError, error }] = useAddReferralMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
    trigger,
  } = useForm<ProviderReferralFormData>({
    resolver: zodResolver(providerReferralSchema),
    mode: "onChange",
    defaultValues: {
      selectedTeeth: [],
    },
  });

  const { handlePhoneChange: handlePatientPhoneChange } = usePhoneFormat(setValue, 'patientPhone');
  const { handlePhoneChange: handleDoctorPhoneChange } = usePhoneFormat(setValue, 'doctorPhone');

  const selectedTeeth = watch('selectedTeeth');


  const handleTeethChange = (teethNumbers: number[]) => {
    setValue('selectedTeeth', teethNumbers);
  };

  const handleFormSubmit = (data: ProviderReferralFormData) => {
    const payload = Object.fromEntries(
      Object.entries({
        patientFirstName: data.patientFirstName,
        patientLastName: data.patientLastName,
        patientPhone: data.patientPhone,
        patientEmail: data.patientEmail,
        referringDoctorName: data.referringDoctorName,
        practiceName: data.practiceName,
        doctorPhone: data.doctorPhone,
        doctorEmail: data.doctorEmail,
        selectedTeeth: data.selectedTeeth,
        remarks: data.remarks,
      }).filter(([_, value]) => {
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        return value !== undefined && value !== null && value !== '';
      })
    );
    addReferral(payload).unwrap().then((res) => {
      toast({
        title: 'Provider referral created successfully',
        description: 'Provider referral created successfully',
      });
      reset();
      setIsOpen(false);
      onSave();
    }).catch((err) => {
      toast({
        title: 'Failed to create provider referral',
        description: err?.messages || 'Failed to create provider referral',
        variant: 'destructive',
      });
    });
  };

  const handleClose = () => {
    reset();
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button type="button" variant="primary" className="p-2 pr-4 h-10">
          <img src="/images/plus-small.svg" alt="Plus" className="flex-shrink-0 mr-1 size-5" />
          Add New Provider Referral
        </Button>
      </DialogTrigger>
      <DialogContent
        className="gap-0 bg-white max-w-[38.75rem] rounded-lg shadow-[0px_1px_4px_0px_rgba(0,0,0,0.25)] overflow-hidden"
        onInteractOutside={e => e.preventDefault()}
      >
        <DialogHeader className="flex flex-row gap-5 justify-between bg-[#F2F4F8] p-4 rounded-t-lg">
          <DialogTitle className="font-serif text-[#5B73F1] text-base font-semibold leading-[1.2rem]">
            Add New Provider Referral
          </DialogTitle>
          <DialogClose asChild>
            <img
              src="/images/close.svg"
              alt="Close"
              className="size-[0.78663rem] cursor-pointer opacity-80 hover:opacity-100 transition-opacity duration-200"
              onClick={handleClose}
            />
          </DialogClose>
        </DialogHeader>
        <form onSubmit={handleSubmit(handleFormSubmit)} className="overflow-y-auto custom-scrollbar h-[calc(100vh-10rem)] max-h-[51.15rem]">
          <div className="px-4 py-3 border-b border-[#DDE1E6]">
            <h5 className="text-[#21272A] text-xs font-semibold leading-[0.9rem] mb-3">
              Patient&apos;s Details
            </h5>
            <div className="grid grid-cols-2 gap-5">
              <div className="">
                <Label
                  htmlFor="patientFirstName"
                  className="w-fit text-[#4D5358] text-xs font-light leading-[0.9rem] mb-1"
                >
                  First Name
                </Label>
                <Input
                  id="patientFirstName"
                  type="text"
                  className={`bg-white h-[2.4375rem] p-3 border-[#DDE1E6] rounded-lg text-[#4D5358] placeholder:text-[#878D96] text-xs font-normal leading-normal focus:outline-none focus:ring-1 focus:ring-blue-200 ${errors.patientFirstName ? 'border-red-500' : ''}`}
                  placeholder="|"
                  {...register('patientFirstName')}
                />
                {errors.patientFirstName && (
                  <p className="text-red-500 text-xs mt-1">{errors.patientFirstName.message}</p>
                )}
              </div>
              <div className="">
                <Label
                  htmlFor="patientLastName"
                  className="w-fit text-[#4D5358] text-xs font-light leading-[0.9rem] mb-1"
                >
                  Last Name
                </Label>
                <Input
                  id="patientLastName"
                  type="text"
                  className={`bg-white h-[2.4375rem] p-3 border-[#DDE1E6] rounded-lg text-[#4D5358] placeholder:text-[#878D96] text-xs font-normal leading-normal focus:outline-none focus:ring-1 focus:ring-blue-200 ${errors.patientLastName ? 'border-red-500' : ''}`}
                  placeholder="|"
                  {...register('patientLastName')}
                />
                {errors.patientLastName && (
                  <p className="text-red-500 text-xs mt-1">{errors.patientLastName.message}</p>
                )}
              </div>
              <div className="">
                <Label
                  htmlFor="patientPhone"
                  className="w-fit text-[#4D5358] text-xs font-light leading-[0.9rem] mb-1"
                >
                  Phone
                </Label>
                <Input
                  id="patientPhone"
                  type="tel"
                  inputMode="numeric"
                  className={`bg-white h-[2.4375rem] p-3 border-[#DDE1E6] rounded-lg text-[#4D5358] placeholder:text-[#878D96] text-xs font-normal leading-normal focus:outline-none focus:ring-1 focus:ring-blue-200 ${errors.patientPhone ? 'border-red-500' : ''}`}
                  placeholder="|"
                  {...register('patientPhone')}
                  onChange={(e) => {
                    handlePatientPhoneChange(e);
                    trigger('patientPhone');
                  }}
                />
                {errors.patientPhone && (
                  <p className="text-red-500 text-xs mt-1">{errors.patientPhone.message}</p>
                )}
              </div>
              <div className="">
                <Label
                  htmlFor="patientEmail"
                  className="w-fit text-[#4D5358] text-xs font-light leading-[0.9rem] mb-1"
                >
                  Email
                </Label>
                <Input
                  id="patientEmail"
                  type="email"
                  className={`bg-white h-[2.4375rem] p-3 border-[#DDE1E6] rounded-lg text-[#4D5358] placeholder:text-[#878D96] text-xs font-normal leading-normal focus:outline-none focus:ring-1 focus:ring-blue-200 ${errors.patientEmail ? 'border-red-500' : ''}`}
                  placeholder="|"
                  {...register('patientEmail')}
                />
                {errors.patientEmail && (
                  <p className="text-red-500 text-xs mt-1">{errors.patientEmail.message}</p>
                )}
              </div>
            </div>
          </div>
          <div className="px-4 py-3 border-b border-[#DDE1E6]">
            <h5 className="text-[#21272A] text-xs font-semibold leading-[0.9rem] mb-3">
              Doctor&apos;s Details
            </h5>
            <div className="grid grid-cols-2 gap-5">
              <div className="">
                <Label
                  htmlFor="referringDoctorName"
                  className="w-fit text-[#4D5358] text-xs font-light leading-[0.9rem] mb-1"
                >
                  Referring Doctor
                </Label>
                <Input
                  id="referringDoctorName"
                  type="text"
                  className={`bg-white h-[2.4375rem] p-3 border-[#DDE1E6] rounded-lg text-[#4D5358] placeholder:text-[#878D96] text-xs font-normal leading-normal focus:outline-none focus:ring-1 focus:ring-blue-200 ${errors.referringDoctorName ? 'border-red-500' : ''}`}
                  placeholder="|"
                  {...register('referringDoctorName')}
                />
                {errors.referringDoctorName && (
                  <p className="text-red-500 text-xs mt-1">{errors.referringDoctorName.message}</p>
                )}
              </div>
              <div className="">
                <Label
                  htmlFor="practiceName"
                  className="w-fit text-[#4D5358] text-xs font-light leading-[0.9rem] mb-1"
                >
                  Practice Name
                </Label>
                <Input
                  id="practiceName"
                  type="text"
                  className={`bg-white h-[2.4375rem] p-3 border-[#DDE1E6] rounded-lg text-[#4D5358] placeholder:text-[#878D96] text-xs font-normal leading-normal focus:outline-none focus:ring-1 focus:ring-blue-200 ${errors.practiceName ? 'border-red-500' : ''}`}
                  placeholder="|"
                  {...register('practiceName')}
                />
                {errors.practiceName && (
                  <p className="text-red-500 text-xs mt-1">{errors.practiceName.message}</p>
                )}
              </div>
              <div className="">
                <Label
                  htmlFor="doctorPhone"
                  className="w-fit text-[#4D5358] text-xs font-light leading-[0.9rem] mb-1"
                >
                  Phone
                </Label>
                <Input
                  id="doctorPhone"
                  type="tel"
                  inputMode="numeric"
                  className={`bg-white h-[2.4375rem] p-3 border-[#DDE1E6] rounded-lg text-[#4D5358] placeholder:text-[#878D96] text-xs font-normal leading-normal focus:outline-none focus:ring-1 focus:ring-blue-200 ${errors.doctorPhone ? 'border-red-500' : ''}`}
                  placeholder="|"
                  {...register('doctorPhone')}
                  onChange={e => {
                    handleDoctorPhoneChange(e);
                    trigger('doctorPhone');
                  }}
                />
                {errors.doctorPhone && (
                  <p className="text-red-500 text-xs mt-1">{errors.doctorPhone.message}</p>
                )}
              </div>
              <div className="">
                <Label
                  htmlFor="doctorEmail"
                  className="w-fit text-[#4D5358] text-xs font-light leading-[0.9rem] mb-1"
                >
                  Email
                </Label>
                <Input
                  id="doctorEmail"
                  type="email"
                  className={`bg-white h-[2.4375rem] p-3 border-[#DDE1E6] rounded-lg text-[#4D5358] placeholder:text-[#878D96] text-xs font-normal leading-normal focus:outline-none focus:ring-1 focus:ring-blue-200 ${errors.doctorEmail ? 'border-red-500' : ''}`}
                  placeholder="|"
                  {...register('doctorEmail')}
                />
                {errors.doctorEmail && (
                  <p className="text-red-500 text-xs mt-1">{errors.doctorEmail.message}</p>
                )}
              </div>
            </div>
          </div>
          <div className="px-4 py-3 border-b border-[#DDE1E6]">
            <ToothSelector 
              teethNumbers={selectedTeeth || []} 
              selectable={true}
              onTeethChange={handleTeethChange}
            />
            {errors.selectedTeeth && (
              <p className="text-red-500 text-xs mt-1">{errors.selectedTeeth.message}</p>
            )}
          </div>
          <div className="px-4 py-3">
            <Label
              htmlFor="remarks"
              className="w-fit text-[#4D5358] text-xs font-light leading-[1.05rem] mb-1"
            >
              Remarks
            </Label>
            <Textarea
              id="remarks"
              className="bg-white min-h-[6.125rem] max-h-[6.125rem] p-3 border-[#DDE1E6] rounded-lg text-[#4D5358] placeholder:text-[#878D96] text-xs font-normal leading-normal focus:outline-none focus:ring-1 focus:ring-blue-200 resize-none"
              placeholder="|"
              {...register('remarks')}
            />
          </div>
          {isError && (
            <div className="text-red-500 text-sm p-3 bg-red-50 rounded-lg mx-4">
              Failed to create provider referral. Please try again.
            </div>
          )}
          <div className="flex justify-end p-4">
            <Button
              onClick={handleSubmit(handleFormSubmit)}
              variant="primary"
              className="w-fit"
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
